import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Snackbar,
  useMediaQuery,
  useTheme,
  Typography,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  Slide,
  Select,
  MenuItem,
  Divider,
} from "@mui/material";
import { useMaterialUIController } from "context";
import CTLDialogContent from "application/components/styles/CTLDialogContent";
import MDButton from "components/MDButton";
import { application } from "globals/endpoints";
import loggerFactory from "globals/logger/logger-factory";
import CTLNotification from "application/components/CTLNotification";

export default function SendSMSDialog({ open, onClose }) {
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
    pagename: "",
    status: "",
  });
  const [loading, setLoading] = useState(false);
  const [selectedGreeting, setSelectedGreeting] = useState("");
  const [controller] = useMaterialUIController();
  const [messageType, setMessageType] = useState([]);
  const { darkMode, openPopup } = controller;
  const [number, setNumber] = useState("");
  const [message, setMessage] = useState("");
  const [mode, setMode] = useState("predefined");
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    setNumber(openPopup);
    application
      .post("predefinedMessage/listAll/SMS")
      .then((response) => {
        setMessageType(response.data.data);
        loggerFactory.debug(
          "SMS Send",
          "Successfully get predefine Message list"
        );
      })
      .catch((error) => {
        setMessageType([]);
        loggerFactory.error("SMS Send", "Error fetching message types:", error);
      });
  }, [open]);

  const handleChange = (e) => {
    setSelectedGreeting(e.target.value);
    setMessage(e.target.value?.messageContent || "");
  };

  const handleClose = () => {
    onClose();
    setMessage("");
    setNumber("");
    setSelectedGreeting("");
    setMode("predefined");
  };

  const handleSubmit = async () => {
    setLoading(true);
    await application
      .post("messagingAgent/sendMessage", {
        destination: number,
        smsText: message,
        messageType: "SMS",
      })
      .then((response) => {
        setNotify({
          isOpen: true,
          status: response.data.message,
          type: "success",
          message: response.data.messageDetail,
          pagename: "SMS",
        });
      })
      .catch((error) => {
        setNotify({
          isOpen: true,
          status: error.response.data.message,
          message: error.response.data.messageDetail,
          type: "error",
          pagename: "SMS",
        });
      });
    setLoading(false);
    handleClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        // fullScreen={fullScreen}
        fullScreen
        fullWidth
        sx={(theme) => CTLDialogContent(theme, { darkMode })}
      >
        <DialogTitle
          sx={{
            ...CTLDialogContent(theme, { darkMode }),
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            Send SMS
          </Typography>
          <IconButton onClick={onClose} size="medium" color="error">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={(theme) => CTLDialogContent(theme, { darkMode })}>
          <Box
            sx={{
              pt: 2,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <TextField
              label="Number"
              type="text"
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              fullWidth
              required
              variant="outlined"
              autoFocus
            />

            {/* Radio Button Section */}
            <RadioGroup
              row
              value={mode}
              onChange={(e) => {
                setMode(e.target.value);
                setMessage("");
                setSelectedGreeting("");
              }}
            >
              <FormControlLabel
                value="predefined"
                control={<Radio />}
                label="Predefined Message"
              />
              <FormControlLabel
                value="custom"
                control={<Radio />}
                label="Custom Message"
              />
            </RadioGroup>

            {/* Slide Animations */}
            <Box
              sx={{
                minHeight: "200px",
                position: "relative",
                overflow: "hidden", // prevents scrollbars from showing
              }}
            >
              {/* Custom message panel */}
              <Slide
                direction="left"
                in={mode === "custom"}
                mountOnEnter
                unmountOnExit
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    pt: 1, // ensure it doesn't overflow
                  }}
                >
                  <TextField
                    label="Message"
                    value={message}
                    placeholder="Type your message here..."
                    onChange={(e) => setMessage(e.target.value)}
                    fullWidth
                    required
                    multiline
                    minRows={6}
                    maxRows={6}
                    variant="outlined"
                  />
                </Box>
              </Slide>

              {/* Predefined message panel */}
              <Slide
                direction="right"
                in={mode === "predefined"}
                mountOnEnter
                unmountOnExit
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    gap: 1,
                  }}
                >
                  <Select
                    value={selectedGreeting}
                    displayEmpty
                    onChange={(e) => handleChange(e)}
                    fullWidth
                    sx={{ mb: 1, height: "45px" }}
                    variant="outlined"
                  >
                    <MenuItem value="">Select Message Type</MenuItem>
                    {messageType?.length > 1 ? (
                      messageType.map((item, index) => (
                        <MenuItem key={index} value={item}>
                          {item.messageType}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>
                        No Predefined Message Available
                      </MenuItem>
                    )}
                  </Select>

                  <TextField
                    label="Message"
                    value={`${message}`}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    multiline
                    minRows={5}
                    maxRows={6}
                    variant="outlined"
                  />
                </Box>
              </Slide>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            ...CTLDialogContent(theme, { darkMode }),
            pb: 2,
          }}
        >
          <MDButton onClick={handleClose} color="secondary" variant="contained">
            Cancel
          </MDButton>
          <MDButton
            onClick={handleSubmit}
            variant="contained"
            color="success"
            disabled={!number || !message || loading}
          >
            {loading ? "Sending..." : "Send SMS"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <CTLNotification notify={notify} setNotify={setNotify} />
    </>
  );
}
